<template>
  <div class="acc-bonus-program-view">
    <breadcrumbs :breadcrumbsData="breadcrumbsData" #icon>
      <svg class="icon" aria-hidden="true" style="color: #fff">
        <use xlink:href="#icon-baifenhao"></use>
      </svg>
    </breadcrumbs>

    <a-spin :spinning="detailLoading">
      <div class="view-content">
        <div class="tabs-wrap">
          <artmisads-segmented-tabs class="tabs-wrap-tabs" v-model="activeTab" :items="tabItems" />
        </div>
        <div class="metric-cards">
            <div class="metric-card">
              <div class="metric-label">{{ t('seller.sellerAccount.colSales') }}</div>
              <div class="metric-value">{{ currencySymbol }} {{ formatMoney(detail.salesAmount) }}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">{{ t('seller.accBonusProgram.colBonus') }}</div>
              <div class="metric-value">{{ currencySymbol }} {{ formatMoney(detail.bonusAmount) }}</div>
            </div>
          </div>
        <!-- Program Info -->
        <div v-show="activeTab === 'info'" class="tab-panel">
        

          <div class="info-panel">
            <div class="info-row">
              <span class="info-label">{{ t('seller.accBonusProgram.colTitle') }}</span>
              <span class="info-value">{{ detail.title || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('seller.common.brand') }}</span>
              <span class="info-value">{{ detail.brandName || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('seller.accBonusProgram.colBonusRule') }}</span>
              <span class="info-value">
                <template v-if="isTierRule">
                  <a class="linkStyle" href="#" @click.prevent="openTierRates">{{
                    t('seller.accBonusProgram.tierRates')
                  }}</a>
                </template>
                <template v-else>{{ bonusRuleText }}</template>
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('seller.accBonusProgram.colPeriod') }}</span>
              <span class="info-value">{{ periodText }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('seller.common.status') }}</span>
              <span class="info-value">
                <Status
                  v-if="detail.status"
                  :type="programStatusType(detail.status)"
                  :text="detail.status"
                  :noBorder="true"
                />
                <template v-else>—</template>
              </span>
            </div>
          </div>
        </div>

        <!-- Campaigns -->
        <div v-show="activeTab === 'campaigns'" class="tab-panel tab-panel-table">
          <artmisads-table
            v-model:pagination="campaignPagination"
            :columns="campaignColumns"
            :data-source="campaignList"
            :change="onCampaignTableChange"
            :loading="campaignLoad"
            :scroll="{ x: 1500 }"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'campaignStatus'">
                <Status
                  :type="campaignStatusType(record.campaignStatus)"
                  :text="campaignStatusText(record.campaignStatus)"
                />
              </template>
              <template v-else-if="column.key === 'dateRange'">
                {{ formatDate(record.startDateTimeInEpochMills) }} -
                {{ formatDate(record.scheduledEndDateTimeInEpochMills) }}
              </template>
              <template v-else-if="column.key === 'accCommission'">
                <template v-if="record.accCommission != null && record.accCommission !== ''">
                  {{ record.accCommission }}%
                </template>
                <template v-else>-</template>
              </template>
              <template v-else-if="column.key === 'budget' || 
              column.key === 'budgetRemaining'||
              column.key === 'salesAmount'||
              column.key === 'bonusAmount'
              ">
                {{ currencySymbol }} {{ formatMoney(record[column.key]) }}
              </template>
            </template>
          </artmisads-table>
        </div>

        <!-- Publishers -->
        <div v-show="activeTab === 'publishers'" class="tab-panel tab-panel-table">
          <artmisads-table
            v-model:pagination="publisherPagination"
            :columns="publisherColumns"
            :data-source="publisherList"
            :change="onPublisherTableChange"
            :loading="publisherLoad"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'publisher'">
                <div class="publisher-cell">
                  <LogoImage :logoUrl="record.avatarUrl" :brandName="record.publisherName" />
                  <span class="publisher-name">{{ record.publisherName }}</span>
                </div>
              </template>
              <template v-else-if="column.key === 'salesAmount' || column.key === 'bonusAmount'">
                {{ currencySymbol }} {{ formatMoney(record[column.key]) }}
              </template>
            </template>
          </artmisads-table>
        </div>
      </div>
    </a-spin>

    <TierRatesModal ref="tierRatesModalRef" />
  </div>
</template>

<script setup>

import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import moment from 'moment'
import { message } from 'ant-design-vue'
import breadcrumbs from '../../breadcrumbs.vue'
import Status from '@/components/Status.vue'
import LogoImage from '@/components/LogoImage.vue'
import TierRatesModal from './components/tierRatesModal.vue'
import { accBonusProgramApi } from '@/server/index'
import { CURRENCY_MAPPER, CURRENCY_DATA_MAPPER } from '@/contants/common'
import { useUserInfoStore } from '@/storeData'

const { t } = useI18n()
const route = useRoute()
const user = useUserInfoStore()
const countryCode = user?.userInfo?.countryCode || 'US'

const programId = computed(() => route.params.id)
const detailLoading = ref(false)
const detail = ref({})
const activeTab = ref('info')
const tierRatesModalRef = ref(null)

const campaignLoad = ref(false)
const campaignList = ref([])
const campaignPagination = reactive({
  total: 0,
  current: 1,
  size: 10,
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50'],
})

const publisherLoad = ref(false)
const publisherList = ref([])
const publisherPagination = reactive({
  total: 0,
  current: 1,
  size: 10,
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50'],
})

const breadcrumbsData = computed(() => ({
  title: t('seller.accBonusProgram.viewTitle'),
  des: '',
  hasTitleIcon: true,
  color: '#26AADC',
  links: [
    { name: t('seller.common.home'), path: '/seller/dashboard' },
    {
      name: t('seller.menu.creatorConnections'),
      path: '/seller/creatorConnections/accBonusProgram',
    },
    {
      name: t('seller.accBonusProgram.pageTitle'),
      path: '/seller/creatorConnections/accBonusProgram',
    },
    {
      name: t('seller.accBonusProgram.viewTitle'),
      path: route.fullPath,
    },
  ],
}))

const tabItems = computed(() => [
  { key: 'info', label: t('seller.accBonusProgram.tabProgramInfo') },
  { key: 'campaigns', label: t('seller.accBonusProgram.tabCampaigns') },
  { key: 'publishers', label: t('seller.accBonusProgram.tabPublishers') },
])

const currencyCode = computed(() => detail.value.currency || 'USD')
const currencySymbol = computed(() => CURRENCY_DATA_MAPPER[currencyCode.value] || '$')

const isTierRule = computed(() => {
  const type = String(detail.value.bonusRuleType || '').toLowerCase()
  return type === 'tier'
})

const bonusRuleText = computed(() => {
  if (detail.value.bonusRuleSummary) return detail.value.bonusRuleSummary
  const rate = detail.value.cpsRate ?? detail.value.fixedCpsRate ?? detail.value.bonusRate
  if (rate != null) {
    return t('seller.accBonusProgram.fixedRateLabel', { rate })
  }
  return '—'
})

const periodText = computed(() => {
  const start = formatDate(
    detail.value.startDateTimestamp ??
      detail.value.startDateInEpochMills ??
      detail.value.startDate,
  )
  const end = formatDate(
    detail.value.endDateTimestamp ?? detail.value.endDateInEpochMills ?? detail.value.endDate,
  )
  if (start === '—' && end === '—') return '—'
  return `${start} - ${end}`
})

const campaignColumns = computed(() => [
  { title: t('seller.accBonusProgram.colName'), dataIndex: 'campaignName', key: 'campaignName' },
  { title: t('seller.common.brand'), dataIndex: 'brandName', key: 'brandName' },
  { title: t('seller.common.status'), dataIndex: 'campaignStatus', key: 'campaignStatus' },
  {
    title: t('seller.accBonusProgram.colDateRange'),
    dataIndex: 'dateRange',
    key: 'dateRange',
    width: 200,
  },
  { title: t('seller.accBonusProgram.colBudget'), dataIndex: 'budget', key: 'budget', width: 200 },
  {
    title: t('seller.accBonusProgram.colBudgetRemaining'),
    dataIndex: 'budgetRemaining',
    key: 'budgetRemaining',
    width: 160,
  },
  {
    title:'ACC Commission',
    dataIndex: 'accCommission',
    key: 'accCommission',
    width: 140,
  },
  {
    title:'Products',
    dataIndex: 'productsCount',
    key: 'productsCount',
    width: 140,
  },
  {
    title:'Sales',
    dataIndex: 'salesAmount',
    key: 'salesAmount',
    width: 140,
  },
  {
    title:'Bonus',
    dataIndex: 'bonusAmount',
    key: 'bonusAmount',
    width: 140,
  }
])

const publisherColumns = computed(() => [
  {
    title: t('seller.menu.publisher'),
    dataIndex: 'publisher',
    key: 'publisher',
    width: 220,
  },
  {
    title: t('seller.sellerAccount.colSales'),
    dataIndex: 'salesAmount',
    key: 'salesAmount',
  },
  {
    title: t('seller.accBonusProgram.colBonusEarned'),
    dataIndex: 'bonusAmount',
    key: 'bonusAmount',
  },
])

const formatMoney = (val) => Number(val ?? 0).toFixed(2)
const formatDate = (ts) => (ts ? moment(ts).format('YYYY-MM-DD') : '—')

const campaignStatusText = (status) => {
  const map = {
    DELIVERING: t('seller.sellerAccount.campaignStatusDelivering'),
    ENDED: t('seller.sellerAccount.campaignStatusEnded'),
    EXPIRED: t('seller.sellerAccount.campaignStatusExpired'),
    OUTOFBUDGET: t('seller.sellerAccount.campaignStatusOutOfBudget'),
    CREATED: t('seller.sellerAccount.campaignStatusCreated'),
    DELETED: t('seller.sellerAccount.campaignStatusDeleted'),
  }
  return map[status] || status
}

const campaignStatusType = (status) => {
  if (status === "ACTIVE" || status === "SCHEDULED" || status === "ENDED") return "active"
  if (status === "EXPIRED") return "finished"
  if (status === "OUT_OF_BUDGET") return "pending"
  if (status === "ENDED_GRACE_PERIOD") return "inactive"
  if (status === "DELETED") return "not_authorized"
  if (status === "DELIVERING" || status === "CREATED") return "delivering"
  return "inactive"
}

const programStatusType = (status) => {
  if (status === 'ACTIVE') return 'active'
  if (status === 'COMPLETED') return 'finished'
  if (status === 'CANCELLED') return 'inactive'
  if (status === 'INCOMING') return 'delivering'
  return 'inactive'
}

const fetchProgramDetail = async () => {
  if (!programId.value) return
  detailLoading.value = true
  try {
    const res = await accBonusProgramApi.getProgramDetail({
      programId: programId.value,
      marketplace: CURRENCY_MAPPER[countryCode].host,
    })
    if (res?.success && res?.data) {
      detail.value = res.data
    } else {
      message.error(res?.error || t('seller.msg.getDataFailed'))
    }
  } catch {
    message.error(t('seller.msg.getDataFailed'))
  } finally {
    detailLoading.value = false
  }
}

const fetchCampaigns = async () => {
  if (!programId.value) return
  campaignLoad.value = true
  try {
    const res = await accBonusProgramApi.getProgramCampaignDetail({
      programId: programId.value,
      page: campaignPagination.current,
      size: campaignPagination.size,
    })
    if (res?.success && res?.data) {
      const list = res.data.data ?? res.data
      campaignList.value = Array.isArray(list) ? list : []
      campaignPagination.total = res.data.total ?? campaignList.value.length
    }
  } catch {
    message.error(t('seller.msg.getDataFailed'))
  } finally {
    campaignLoad.value = false
  }
}

const fetchPublishers = async () => {
  if (!programId.value) return
  publisherLoad.value = true
  try {
    const res = await accBonusProgramApi.getProgramPublisherDetail({
      programId: programId.value,
      page: publisherPagination.current,
      size: publisherPagination.size,
    })
    if (res?.success && res?.data) {
      const list = res.data.data ?? res.data
      publisherList.value = Array.isArray(list) ? list : []
      publisherPagination.total = res.data.total ?? publisherList.value.length
    }
  } catch {
    message.error(t('seller.msg.getDataFailed'))
  } finally {
    publisherLoad.value = false
  }
}

const campaignsTabLoaded = ref(false)
const publishersTabLoaded = ref(false)

watch(activeTab, (key) => {
  if (key === 'campaigns' ) {
    campaignsTabLoaded.value = true
    fetchCampaigns()
  }
  if (key === 'publishers' ) {
    publishersTabLoaded.value = true
    fetchPublishers()
  }
})

const onCampaignTableChange = ({ current, pageSize }) => {
  campaignPagination.current = current
  campaignPagination.size = pageSize
  campaignPagination.pageSize = pageSize
  fetchCampaigns()
}

const onPublisherTableChange = ({ current, pageSize }) => {
  publisherPagination.current = current
  publisherPagination.size = pageSize
  publisherPagination.pageSize = pageSize
  fetchPublishers()
}

const openTierRates = () => {
  tierRatesModalRef.value?.show({
    programId: programId.value,
    tierRates: detail.value.tierRates ?? [],
    currency: currencyCode.value,
  })
}

onMounted(() => {
  fetchProgramDetail()
})
</script>

<style lang="less" scoped>
.acc-bonus-program-view {
  padding: 0 16px 16px;
  padding-top: 0;
}

.view-content {
  background: #fff;
  border-radius: 8px;
  box-shadow: var(--section-box-shadow-6);
  padding: 16px 20px 24px;
  min-height: 400px;
}

.tabs-wrap {
  // max-width: 520px;
  margin-bottom: 20px;
}
.tabs-wrap-tabs{
  margin: 0px;
}
.tab-panel {
  min-height: 280px;
}

.metric-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.metric-card {
  width: 200px;
  min-height: 61.5px;
  user-select: none;
  box-sizing: border-box;
  background-color: var(--bg-gray-8);
  border-radius: 8px;
  padding: 8px 7px 7px 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.metric-label {
  font-weight: 500;
    font-size: 13px;
    line-height: 20px;
    letter-spacing: 0.3px;
}

.metric-value {
  font-family: Sora, sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: rgba(15, 6, 41, 1);
  letter-spacing: 0.4px;
  margin-top: 4px;
}

.info-panel {
  border: 1px solid #e8e5ed;
  border-radius: 8px;
  padding: 20px 24px;
  // background: #fafafa;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;

  // &:not(:last-child) {
  //   border-bottom: 1px solid #f0eef3;
  // }
}

.info-label {
  flex: 0 0 120px;
  font-family: Sora, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: rgba(15, 6, 41, 0.65);
}

.info-value {
  flex: 1;
  font-family: Sora, sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: rgba(15, 6, 41, 0.9);
  word-break: break-word;
}

.linkStyle {
  color: #7653da;
  text-decoration: underline;
}

.tab-panel-table {
  padding-top: 4px;
}

.publisher-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.publisher-name {
  font-size: 14px;
  color: #0f0629;
}
</style>
